import React, { useState, useEffect, useCallback } from 'react';
import { getAdminJobs } from '../../api'; // Keep retryJob for now, hook uses post directly
import { Link, useSearch, useNavigate } from '@tanstack/react-router';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import Breadcrumbs from '../../components/Breadcrumbs';
import Pagination from '../../components/Pagination'; // Import Pagination component
import { useRetryJob } from '../../hooks/useRetryJob'; // Import the hook

// Helper to format bytes
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0 || !bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Helper for status badge styling (semantic tokens)
const getStatusClass = (status) => {
    switch (status) {
        case 'SUCCESS':
            return 'bg-success-soft text-success-soft-foreground border border-success-border';
        case 'FAILED':
            return 'bg-danger-soft text-danger-soft-foreground border border-danger-border';
        case 'PROCESSING':
            return 'bg-info-soft text-info-soft-foreground border border-info-border';
        case 'PENDING':
            return 'bg-warning-soft text-warning-soft-foreground border border-warning-border';
        default:
            return 'bg-background-soft text-muted-foreground border border-border';
    }
};

const AdminJobsPage = () => {
    const [jobs, setJobs] = useState([]);
    const [totalJobs, setTotalJobs] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { user_id: userIdFilter, username: usernameFilter } = useSearch({ });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);

    // Wrap fetchJobs in useCallback as it's used in useEffect and setTimeout
    const fetchJobs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const skip = (currentPage - 1) * pageSize;
            const response = await getAdminJobs(userIdFilter, pageSize, skip);
            setJobs(response.jobs || []);
            setTotalJobs(response.total || 0);
        } catch (err) {
            console.error("Failed to fetch jobs:", err);
            setError(err.message || 'Failed to load jobs.');
        } finally {
            setLoading(false);
        }
    }, [userIdFilter, currentPage, pageSize]); // Include pagination dependencies
    
    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]); // Add fetchJobs to dependency array

    // Reset to first page when user filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [userIdFilter]);

    // Calculate total pages for pagination
    const totalPages = Math.ceil(totalJobs / pageSize);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // --- Setup Retry Hook ---
    const handleRetrySuccess = useCallback(() => {
      // Simply refetch the jobs list after a short delay to see the updated status
      setTimeout(fetchJobs, 500); 
    }, [fetchJobs]);

    const { retryingJobs, retryError, triggerRetry } = useRetryJob(handleRetrySuccess);

    // Define breadcrumbs
    const crumbs = [
        { label: 'Admin', to: '/app/admin' },
        { label: 'Jobs' }, // Current page
    ];
    // Add user filter crumb if active
    if (userIdFilter) {
        crumbs.splice(1, 0, { label: 'Users', to: '/app/admin/users' });
        crumbs[2].label = `Jobs for ${usernameFilter || userIdFilter}`;
        // Make the final crumb not a link if filtering
        // crumbs[2].to = null; 
    }

    return (
        <div className="text-foreground">
            <Breadcrumbs crumbs={crumbs} />

            {/* Conditional rendering for loading state */}
            {loading && <div className="text-foreground">Loading jobs...</div>}

            {/* Conditional rendering for error state */}
            {error && <div className="text-danger">Error: {error}</div>}

            {/* Display retry error from the hook */}
                        {retryError && (
                            <div className="my-4 text-danger text-sm p-3 bg-danger-soft border border-danger-border rounded">
                {retryError}
              </div>
            )}

            {/* Main content shown only when not loading and no error */}
            {!loading && !error && (
                <>
                    <h1 className="text-2xl font-semibold mb-6">
                        {userIdFilter ? `Jobs for ${usernameFilter || userIdFilter}` : 'All jobs'}
                    </h1>
            
            {userIdFilter && (
                 <div className="mb-4">
                    <Link 
                        to="/app/admin/jobs" 
                        className="text-sm text-accent hover:underline hover:text-accent/90"
                    >
                        Show all jobs
                    </Link>
                 </div>
             )}
            
            <div className="bg-background-elevated border border-border shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr className="bg-background-soft text-left text-muted-foreground uppercase text-sm">
                            <th className="px-5 py-3 border-b-2 border-border">Job ID</th>
                            <th className="px-5 py-3 border-b-2 border-border">Filename</th>
                            <th className="px-5 py-3 border-b-2 border-border">User ID</th>
                            <th className="px-5 py-3 border-b-2 border-border">Status</th>
                            <th className="px-5 py-3 border-b-2 border-border">Original Size</th>
                            <th className="px-5 py-3 border-b-2 border-border">Compressed Size</th>
                            <th className="px-5 py-3 border-b-2 border-border">Ratio</th>
                            <th className="px-5 py-3 border-b-2 border-border">Created At</th>
                            <th className="px-5 py-3 border-b-2 border-border">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((job) => (
                            <tr 
                                key={job.id} 
                                className="hover:bg-background-soft cursor-pointer"
                                onClick={() => navigate({ to: '/app/admin/jobs/$jobId', params: { jobId: job.id } })}
                            >
                                <td className="px-5 py-4 border-b border-border text-sm text-accent">
                                    <code>{job.id.substring(0, 8)}...</code>
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">
                                    {job.file_name}
                                </td>
                                 <td className="px-5 py-4 border-b border-border text-sm">
                                     {/* Link to the user page, passing user_id and username */}
                                     <Link 
                                         to="/app/admin/users"
                                         search={{ user_id: job.user_id, username: job.username }} 
                                         className="text-accent hover:underline hover:text-accent/90"
                                         title={`View user: ${job.username || job.user_id}`}
                                         onClick={(e) => e.stopPropagation()} // Prevent row click navigation
                                     >
                                         {/* Display username if available, otherwise truncated user_id */}
                                         {job.username ? job.username : <code>{job.user_id.substring(0, 8)}...</code>}
                                     </Link>
                                </td>
                          <td className="px-5 py-4 border-b border-border text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusClass(job.status)}`}>
                                        {job.status}
                                    </span>
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm">
                                    {formatBytes(job.original_file_size)}
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm">
                                    {formatBytes(job.compressed_file_size)}
                                </td>
                                 <td className="px-5 py-4 border-b border-border text-sm">
                                    {job.compression_ratio ? `${(job.compression_ratio * 100).toFixed(1)}%` : 'N/A'}
                                </td>
                                <td className="px-5 py-4 border-b border-border text-sm whitespace-nowrap">
                                    {new Date(job.created_at).toLocaleString()}
                                </td>
                                <td 
                                    className="px-5 py-4 border-b border-border text-sm whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                     {job.status === 'FAILED' && (
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); triggerRetry(job.id); }}
                                            disabled={retryingJobs.has(job.id)} // Check if job ID is in the Set
                                            className={`bg-warning hover:bg-warning/90 text-warning-foreground text-xs font-bold py-1 px-2 rounded inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed`}
                                            title="Retry Job"
                                        >
                                            <ArrowPathIcon className={`w-4 h-4 mr-1 ${retryingJobs.has(job.id) ? 'animate-spin' : ''}`}/>
                                            {retryingJobs.has(job.id) ? 'Retrying...' : 'Retry'}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {jobs.length === 0 && !loading && (
                     <div className="p-4 text-center text-muted-foreground">
                        No jobs found{userIdFilter ? ` for user ${usernameFilter || userIdFilter}` : ''}.
                    </div>
                )}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    paginate={handlePageChange}
                />
            )}
        </>
    )}
        </div>
    );
};

export default AdminJobsPage; 